import type {
  CustomEasingCurve,
  EasingType,
} from '../../project/ProjectDocument.js'

export const CUSTOM_EASING_MIN_Y = -2
export const CUSTOM_EASING_MAX_Y = 3

const DEFAULT_CUSTOM_P1_X = 0.25
const DEFAULT_CUSTOM_P1_Y = 0.1
const DEFAULT_CUSTOM_P2_X = 0.25
const DEFAULT_CUSTOM_P2_Y = 1

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function assertEasingType(easingType: EasingType): void {
  switch (easingType) {
    case 'linear':
    case 'smooth':
    case 'bounce':
    case 'elastic':
    case 'expo':
    case 'back':
    case 'cubic':
    case 'custom':
      return
    default:
      throw new RangeError(`Unknown easing type: ${String(easingType)}`)
  }
}

function bezierPoint(t: number, p1: number, p2: number): number {
  const remaining = 1 - t
  return 3 * remaining * remaining * t * p1
    + 3 * remaining * t * t * p2
    + t * t * t
}

function bezierDerivative(t: number, p1: number, p2: number): number {
  const remaining = 1 - t
  return 3 * remaining * remaining * p1
    + 6 * remaining * t * (p2 - p1)
    + 3 * t * t * (1 - p2)
}

function evaluateCubicBezier(
  progress: number,
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
): number {
  if (progress <= 0) return 0
  if (progress >= 1) return 1

  // Reflect the upper half around (0.5, 0.5). This avoids catastrophic loss of
  // precision when a flat endpoint makes x extremely close to one.
  const reflected = progress > 0.5
  const target = reflected ? 1 - progress : progress
  const control1X = reflected ? 1 - p2x : p1x
  const control1Y = reflected ? 1 - p2y : p1y
  const control2X = reflected ? 1 - p1x : p2x
  const control2Y = reflected ? 1 - p1y : p2y
  let lower = 0
  let upper = 1
  let parameter = target

  // Maintain a monotonic bracket on every iteration. Newton is used only when
  // its candidate remains safeguarded inside that bracket; otherwise bisection
  // makes guaranteed progress even for legal x=t³ / x=1-(1-t)³ endpoints.
  for (let iteration = 0; iteration < 12; iteration += 1) {
    const currentX = bezierPoint(parameter, control1X, control2X)
    if (currentX === target) {
      lower = parameter
      upper = parameter
      break
    }
    if (currentX < target) lower = parameter
    else upper = parameter

    const derivative = bezierDerivative(parameter, control1X, control2X)
    const candidate = Math.abs(derivative) > 1e-14
      ? parameter - (currentX - target) / derivative
      : Number.NaN
    parameter = Number.isFinite(candidate) && candidate > lower && candidate < upper
      ? candidate
      : (lower + upper) * 0.5
  }

  // Refine by parameter width, not absolute x error. Absolute x error is a
  // misleading convergence signal where an accepted curve has a flat endpoint.
  for (let iteration = 0; iteration < 52; iteration += 1) {
    const currentX = bezierPoint(parameter, control1X, control2X)
    if (currentX < target) lower = parameter
    else upper = parameter
    parameter = (lower + upper) * 0.5
    if (upper - lower <= 1e-15) break
  }

  const value = bezierPoint(parameter, control1Y, control2Y)
  return reflected ? 1 - value : value
}

function bounceOut(progress: number): number {
  const scale = 7.5625
  const divisor = 2.75
  if (progress < 1 / divisor) return scale * progress * progress
  if (progress < 2 / divisor) {
    const shifted = progress - 1.5 / divisor
    return scale * shifted * shifted + 0.75
  }
  if (progress < 2.5 / divisor) {
    const shifted = progress - 2.25 / divisor
    return scale * shifted * shifted + 0.9375
  }
  const shifted = progress - 2.625 / divisor
  return scale * shifted * shifted + 0.984375
}

/**
 * Validate the persisted custom cubic-bezier domain.
 *
 * X control points are CSS-compatible time coordinates. Y may overshoot, but
 * is bounded to the same -2…3 range accepted by the V1 exporter/migration.
 */
export function validateCustomEasingCurve(
  curve: CustomEasingCurve,
): CustomEasingCurve {
  const p1x = curve.p1.x
  const p1y = curve.p1.y
  const p2x = curve.p2.x
  const p2y = curve.p2.y
  if (
    !Number.isFinite(p1x)
    || !Number.isFinite(p1y)
    || !Number.isFinite(p2x)
    || !Number.isFinite(p2y)
  ) {
    throw new TypeError('Custom easing control points must be finite')
  }
  if (
    p1x < 0 || p1x > 1
    || p2x < 0 || p2x > 1
    || p1y < CUSTOM_EASING_MIN_Y || p1y > CUSTOM_EASING_MAX_Y
    || p2y < CUSTOM_EASING_MIN_Y || p2y > CUSTOM_EASING_MAX_Y
  ) {
    throw new RangeError('Custom easing control points are outside supported bounds')
  }
  return curve
}

/** Evaluate one of the eight canonical V2 easing curves. */
export function evaluateEasing(
  progress: number,
  easingType: EasingType,
  customCurve?: CustomEasingCurve,
): number {
  assertEasingType(easingType)
  if (!Number.isFinite(progress)) {
    throw new TypeError('Easing progress must be finite')
  }
  if (easingType === 'custom' && customCurve !== undefined) {
    validateCustomEasingCurve(customCurve)
  }
  const t = clamp01(progress)
  if (t === 0 || t === 1 || easingType === 'linear') return t

  switch (easingType) {
    case 'smooth': {
      const smooth = t * t * (3 - 2 * t)
      return smooth < 0.5
        ? 16 * smooth * smooth * smooth * smooth * smooth
        : 1 - Math.pow(-2 * smooth + 2, 5) / 2
    }
    case 'bounce':
      return t < 0.5
        ? (1 - bounceOut(1 - 2 * t)) / 2
        : (1 + bounceOut(2 * t - 1)) / 2
    case 'elastic': {
      const period = (2 * Math.PI) / 4.5
      return t < 0.5
        ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * period)) / 2
        : (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * period)) / 2 + 1
    }
    case 'expo':
      return t < 0.5
        ? Math.pow(2, 20 * t - 10) / 2
        : (2 - Math.pow(2, -20 * t + 10)) / 2
    case 'back': {
      const overshoot = 1.70158 * 1.525
      return t < 0.5
        ? (Math.pow(2 * t, 2) * ((overshoot + 1) * 2 * t - overshoot)) / 2
        : (Math.pow(2 * t - 2, 2) * ((overshoot + 1) * (2 * t - 2) + overshoot) + 2) / 2
    }
    case 'cubic':
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2
    case 'custom': {
      if (customCurve === undefined) {
        return evaluateCubicBezier(
          t,
          DEFAULT_CUSTOM_P1_X,
          DEFAULT_CUSTOM_P1_Y,
          DEFAULT_CUSTOM_P2_X,
          DEFAULT_CUSTOM_P2_Y,
        )
      }
      return evaluateCubicBezier(
        t,
        customCurve.p1.x,
        customCurve.p1.y,
        customCurve.p2.x,
        customCurve.p2.y,
      )
    }
  }
}

/** Blend continuously from linear motion (0) to the selected easing (1). */
export function applyEasing(
  progress: number,
  easingType: EasingType,
  intensity: number,
  customCurve?: CustomEasingCurve,
): number {
  assertEasingType(easingType)
  if (!Number.isFinite(progress)) {
    throw new TypeError('Easing progress must be finite')
  }
  if (!Number.isFinite(intensity)) {
    throw new TypeError('Easing intensity must be finite')
  }
  if (easingType === 'custom' && customCurve !== undefined) {
    validateCustomEasingCurve(customCurve)
  }
  const t = clamp01(progress)
  const blend = clamp01(intensity)
  if (blend === 0 || easingType === 'linear') return t
  const eased = evaluateEasing(t, easingType, customCurve)
  if (t === 0 || t === 1) return t
  return t + (eased - t) * blend
}
